import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiCall } from '../../helper/Common';
import { SHOPIFY_API_KEY, buildNodeApiEntryUrl } from '../../config/env';

/**
 * Helper function to handle redirect on install failure
 * Uses App Bridge redirect if available, falls back to window navigation
 */
const handleRedirect = (shop, host) => {
  if (!shop) {
    console.warn('[handleRedirect] Shop parameter missing, cannot redirect');
    return;
  }

  const redirectUrl = buildNodeApiEntryUrl(shop, host);

  // Try App Bridge redirect first (for embedded apps)
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const appHost = host || urlParams.get('host');

    if (appHost && SHOPIFY_API_KEY) {
      try {
        const createApp = require('@shopify/app-bridge').default;
        const { Redirect } = require('@shopify/app-bridge/actions');

        const app = createApp({
          apiKey: SHOPIFY_API_KEY,
          host: appHost,
        });
        const redirect = Redirect.create(app);
        redirect.dispatch(Redirect.Action.REMOTE, redirectUrl);
        return;
      } catch (appBridgeError) {
        console.warn('[handleRedirect] App Bridge redirect failed:', appBridgeError);
      }
    }
  } catch (redirectError) {
    console.warn('[handleRedirect] App Bridge redirect failed, trying fallback:', redirectError);
  }

  // Fallback: Try safe navigation methods
  try {
    if (window.top && window.top !== window) {
      window.top.location.href = redirectUrl;
    } else {
      window.location.href = redirectUrl;
    }
  } catch (navError) {
    console.error('[handleRedirect] Navigation failed, opening in new window:', navError);
    window.open(redirectUrl, '_blank');
  }
};

/**
 * Helper function to extract shop and host from URL
 */
const extractShopAndHost = () => {
  const urlParams = new URLSearchParams(window.location.search);
  let shop = urlParams.get('shop');
  let host = urlParams.get('host');

  // Extract from current URL if not in params
  if (!shop) {
    const currentUrl = window.location.href;
    const shopMatch = currentUrl.match(/[?&]shop=([^&]+)/);
    if (shopMatch) {
      shop = decodeURIComponent(shopMatch[1]);
    }
    const hostMatch = currentUrl.match(/[?&]host=([^&]+)/);
    if (hostMatch && !host) {
      host = decodeURIComponent(hostMatch[1]);
    }
  }

  return { shop, host };
};

/**
 * Async thunk to check install status
 * 
 * OPTIMIZATION: Only calls install API if data is not already in Redux.
 * This prevents unnecessary API calls when:
 * - Install data already exists in Redux
 * - Shop parameter matches existing shop
 * - Install check has already been completed
 * 
 * On failure: redirects to install URL
 * 
 * Usage: Components should use useSelector to access install state from Redux.
 * The install check is automatically dispatched once when the store initializes.
 */
export const checkInstall = createAsyncThunk(
  'install/checkInstall',
  async (arg, { getState, rejectWithValue }) => {
    const forceRefresh =
      arg === true || (arg && typeof arg === 'object' && arg.force === true);

    // Check if install data already exists in Redux
    const state = getState();
    const existingInstallData = state.install.installData;
    const existingShop = state.install.shop;
    const existingHost = state.install.host;
    const isAlreadyChecked = state.install.checked;
    
    // Extract shop and host from URL
    const { shop, host } = extractShopAndHost();

    // If install data exists and shop matches, skip API call
    // This prevents unnecessary API calls when data is already in Redux
    if (
      !forceRefresh &&
      existingInstallData &&
      existingShop === shop &&
      isAlreadyChecked
    ) {
      return {
        success: true,
        shop: existingShop || shop,
        host: existingHost || host,
        data: existingInstallData,
        message: 'Using cached install data'
      };
    }

    if (!shop) {
      return rejectWithValue('Shop parameter is missing. Please access the app from Shopify admin.');
    }

    // Call install API only if data doesn't exist
    try {
      const response = await apiCall('install', 'POST', { shop });

      if (response?.success || response?.message === 'success' || response?.data) {
        return {
          success: true,
          shop: shop,
          host: host,
          data: response.data || response,
          message: response.message || 'Install check successful'
        };
      }

      // API call failed - redirect to install page
      handleRedirect(shop, host);
      return rejectWithValue(response?.message || 'Install API call failed');

    } catch (error) {

      // Extract error message
      let errorMessage = 'App not installed';
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // Redirect on failure
      handleRedirect(shop, host);

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Install slice - manages install state in Redux
 */
const installSlice = createSlice({
  name: 'install',
  initialState: {
    checked: false,
    success: false,
    loading: false,
    error: null,
    shop: null,
    host: null,
    installData: null,
  },
  reducers: {
    /**
     * Reset install state (useful for testing or logout)
     */
    resetInstall: (state) => {
      state.checked = false;
      state.success = false;
      state.loading = false;
      state.error = null;
      state.shop = null;
      state.host = null;
      state.installData = null;
    },
    /**
     * Set shop and host manually (useful for edge cases)
     */
    setShopAndHost: (state, action) => {
      state.shop = action.payload.shop;
      state.host = action.payload.host;
    },
    /**
     * Update onboarding status in installData
     * This keeps Redux in sync with backend when onboarding status changes
     */
    updateOnboardingStatus: (state, action) => {
      const { onboardingStatus } = action.payload;
      if (state.installData && state.installData.shop_info) {
        state.installData.shop_info.is_on_boarding = String(onboardingStatus);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Pending: Set loading state
      .addCase(checkInstall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Fulfilled: Store install data in Redux
      .addCase(checkInstall.fulfilled, (state, action) => {
        state.loading = false;
        state.checked = true;
        state.success = true;
        state.shop = action.payload.shop;
        state.host = action.payload.host;
        state.installData = action.payload.data;
        state.error = null;
      })
      // Rejected: Set error state
      .addCase(checkInstall.rejected, (state, action) => {
        state.loading = false;
        state.checked = true;
        state.success = false;
        state.error = action.payload || 'Install check failed';

        // Preserve shop/host for redirect
        const { shop, host } = extractShopAndHost();
        state.shop = state.shop || shop;
        state.host = state.host || host;
      });
  },
});

export const { resetInstall, setShopAndHost, updateOnboardingStatus } = installSlice.actions;

/**
 * Selector helper to get client_id from install data
 * Returns the shop_info.id from installData, or fallback to 1
 */
export const selectClientId = (state) => {
  return state.install.installData?.shop_info?.id || 1;
};

export default installSlice.reducer;
